import { useEffect, useMemo, useState } from 'react'
import ResellerShell from './ResellerShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

// Default customer-email message per language (mirrors sp_promo_msg_default in
// languagetext). Used so the box can pre-fill in the chosen EMAIL language,
// which can differ from the reseller's portal language.
const DEFAULT_MSG = {
  en: 'Here is a promo code just for you — use it at checkout to save on your next golf getaway. Enjoy!',
  fr: 'Voici un code promo rien que pour vous — utilisez-le au paiement pour économiser sur votre prochaine sortie de golf. Profitez-en!',
}

/**
 * Reseller Promo Codes. Create discount / promo codes for your own embed
 * checkout, scoped to your own packages: a $ off (with a minimum spend), a % off
 * (with a max-discount cap), or a free add-on (e.g. a golf cart). Set a validity
 * window, how many times it can be used, and which packages it applies to.
 */
export default function ResellerPromoCodes() {
  const { t, langId } = useLanguage()
  const [rows, setRows] = useState(null)
  const [meta, setMeta] = useState({ addonTypes: [], packages: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [msg, setMsg] = useState('')
  const [editing, setEditing] = useState(null) // code row or {} for new

  function load() {
    setLoading(true)
    Promise.all([
      tournamentApi.get('/portal/reseller/promo-codes'),
      tournamentApi.get('/portal/reseller/promo-codes/meta'),
    ]).then(([r, m]) => { setRows(r.data); setMeta(m.data); setError(null) })
      .catch(e => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  async function toggleActive(row) {
    try {
      await tournamentApi.patch(`/portal/reseller/promo-codes/${row.ID_NO}`, { ...toPayload(row), isActive: !row.IsActive })
      load()
    } catch (e) { setMsg(`⚠ ${e?.response?.data?.message || e.message}`) }
  }
  async function remove(row) {
    if (!confirm(t('sp_promo_delete_confirm', 'Delete code “{code}”? This can’t be undone.', { code: row.Code }))) return
    try { await tournamentApi.delete(`/portal/reseller/promo-codes/${row.ID_NO}`); setMsg(t('sp_promo_deleted', 'Code deleted.')); load() }
    catch (e) { setMsg(`⚠ ${e?.response?.data?.message || e.message}`) }
  }
  async function resend(row) {
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/promo-codes/${row.ID_NO}/resend`)
      setMsg(data?.emailSent ? t('sp_promo_emailed', 'Code emailed to {email}.', { email: data.email }) : t('sp_promo_email_failed', 'Could not send the email — check your email integration.'))
    } catch (e) { setMsg(`⚠ ${e?.response?.data?.message || e.message}`) }
  }

  const addonLabel = (key) => {
    const a = meta.addonTypes.find(x => x.key === key)
    return a ? (langId === 2 ? a.labelFr : a.labelEn) : key
  }
  const money = (n) => `$${Number(n).toFixed(2)}`
  function benefitSummary(r) {
    if (r.BenefitType === 'percent') return `${Number(r.PercentOff)}% ${t('sp_promo_off', 'off')}${r.MaxDiscount != null ? ` (${t('sp_promo_max', 'max')} ${money(r.MaxDiscount)})` : ''}`
    if (r.BenefitType === 'amount') return `${money(r.AmountOff)} ${t('sp_promo_off', 'off')}${r.MinSpend != null ? ` (${t('sp_promo_min', 'min')} ${money(r.MinSpend)})` : ''}`
    return `${t('sp_promo_free', 'Free')} ${addonLabel(r.AddonType)}`
  }
  function usageSummary(r) {
    const mode = r.UsageMode === 'single' ? t('sp_promo_use_single', 'Single use')
      : r.UsageMode === 'once_per_customer' ? t('sp_promo_use_once', 'Once per customer')
      : t('sp_promo_use_unlimited', 'Unlimited')
    return `${mode} · ${t('sp_promo_used_n', '{n} used', { n: r.RedemptionCount || 0 })}`
  }
  function windowSummary(r) {
    if (!r.StartsAt && !r.EndsAt) return t('sp_promo_no_expiry', 'No expiry')
    const d = (x) => x ? String(x).slice(0, 10) : '…'
    return `${d(r.StartsAt)} → ${d(r.EndsAt)}`
  }

  return (
    <ResellerShell>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24 }}>{t('sp_nav_promo_codes', 'Promo Codes')}</h1>
            <p style={{ color: '#64748b', marginTop: 6, marginBottom: 0, fontSize: 14, maxWidth: 680 }}>
              {t('sp_promo_subtitle', 'Create discount codes customers can enter at checkout on your packages. You absorb the discount; it applies before tax.')}
            </p>
          </div>
          <button onClick={() => setEditing({})} style={primaryBtn}>{t('sp_promo_new', '+ New code')}</button>
        </div>

        {error && <div style={errorBox}>{error}</div>}
        {msg && <div style={okBox}>{msg}</div>}

        {loading ? <div style={empty}>{t('loading_ellipsis', 'Loading…')}</div> : (
          rows.length === 0 ? (
            <div style={empty}>{t('sp_promo_empty', 'No promo codes yet. Create one to offer a discount at checkout.')}</div>
          ) : (
            <div style={card}>
              <table style={tbl}>
                <thead><tr><th style={th}>{t('sp_promo_col_code', 'Code')}</th><th style={th}>{t('sp_promo_col_benefit', 'Benefit')}</th>
                  <th style={th}>{t('sp_promo_col_applies', 'Applies to')}</th><th style={th}>{t('sp_promo_col_window', 'Window')}</th>
                  <th style={th}>{t('sp_promo_col_usage', 'Usage')}</th><th style={th}>{t('sp_promo_col_active', 'Active')}</th>
                  <th style={{ ...th, textAlign: 'right' }}></th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.ID_NO} style={tr}>
                      <td style={td}><strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{r.Code}</strong></td>
                      <td style={td}>{benefitSummary(r)}</td>
                      <td style={td}>{r.AppliesTo === 'all' ? t('sp_promo_all_packages', 'All packages') : t('sp_promo_n_packages', '{n} packages', { n: (r.packageIds || []).length })}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 13, color: '#475569' }}>{windowSummary(r)}</td>
                      <td style={{ ...td, fontSize: 13, color: '#475569' }}>{usageSummary(r)}</td>
                      <td style={td}><Toggle checked={!!r.IsActive} onChange={() => toggleActive(r)} /></td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.AssignedEmail && <button onClick={() => resend(r)} style={linkBtn} title={r.AssignedEmail}>{t('sp_promo_resend', 'Resend')}</button>}
                        <button onClick={() => setEditing(r)} style={linkBtn}>{t('edit', 'Edit')}</button>
                        <button onClick={() => remove(r)} style={{ ...linkBtn, color: '#b91c1c' }}>{t('sp_remove', 'Remove')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {editing && (
        <CodeModal code={editing.ID_NO ? editing : null} meta={meta} langId={langId}
          onClose={() => setEditing(null)}
          onSaved={(m) => { setEditing(null); setMsg(m); load() }} />
      )}
    </ResellerShell>
  )
}

// Build the API payload from a row (used by the inline active-toggle).
function toPayload(r) {
  return {
    code: r.Code, description: r.Description, benefitType: r.BenefitType,
    amountOff: r.AmountOff, percentOff: r.PercentOff, maxDiscount: r.MaxDiscount, minSpend: r.MinSpend,
    addonType: r.AddonType, appliesTo: r.AppliesTo, packageIds: r.packageIds || [],
    startsAt: r.StartsAt ? String(r.StartsAt).slice(0, 10) : null,
    endsAt: r.EndsAt ? String(r.EndsAt).slice(0, 10) : null,
    usageMode: r.UsageMode, maxRedemptions: r.MaxRedemptions, isActive: !!r.IsActive,
  }
}

function CodeModal({ code, meta, langId, onClose, onSaved }) {
  const { t } = useLanguage()
  const [f, setF] = useState(() => ({
    code: code?.Code || '', description: code?.Description || '',
    benefitType: code?.BenefitType || 'percent',
    amountOff: code?.AmountOff ?? '', percentOff: code?.PercentOff ?? '',
    maxDiscount: code?.MaxDiscount ?? '', minSpend: code?.MinSpend ?? '',
    addonType: code?.AddonType || (meta.addonTypes[0]?.key || 'golf_cart'),
    appliesTo: code?.AppliesTo || 'all',
    packageIds: new Set(code?.packageIds || []),
    startsAt: code?.StartsAt ? String(code.StartsAt).slice(0, 10) : '',
    endsAt: code?.EndsAt ? String(code.EndsAt).slice(0, 10) : '',
    usageMode: code?.UsageMode || 'unlimited',
    assignedEmail: code?.AssignedEmail || '',
    emailLang: code?.CustomerLang || (langId === 2 ? 2 : 1), // language the customer email is sent in
    customerMessage: code?.CustomerMessage || DEFAULT_MSG[(code?.CustomerLang || langId) === 2 ? 'fr' : 'en'],
    sendEmailToCustomer: !code, // default on for a new code, off when editing (avoid re-sending)
    isActive: code ? !!code.IsActive : true,
  }))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [labelFilter, setLabelFilter] = useState('') // filter the package picker by grouping label
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }))
  // Switch the email language; swap the default message to that language too,
  // but only if the reseller hasn't written their own.
  function setEmailLang(newLang) {
    setF(prev => {
      const isDefault = !prev.customerMessage.trim() || prev.customerMessage === DEFAULT_MSG.en || prev.customerMessage === DEFAULT_MSG.fr
      return { ...prev, emailLang: newLang, customerMessage: isDefault ? DEFAULT_MSG[newLang === 2 ? 'fr' : 'en'] : prev.customerMessage }
    })
  }

  function togglePkg(id) {
    setF(prev => { const s = new Set(prev.packageIds); s.has(id) ? s.delete(id) : s.add(id); return { ...prev, packageIds: s } })
  }

  // Random one-time code: prompts for a length (8–40) and fills an unambiguous
  // alphanumeric string (omits look-alikes 0/O/1/I/L). Defaults usage to single.
  function generateCode() {
    const MAXLEN = 40
    const raw = window.prompt(t('sp_promo_gen_prompt', 'Code length? (8-{max} characters)', { max: MAXLEN }), '16')
    if (raw == null) return
    let len = parseInt(raw, 10)
    if (!Number.isFinite(len)) len = 16
    len = Math.max(8, Math.min(MAXLEN, len))
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
    let out = ''
    if (window.crypto?.getRandomValues) {
      const a = new Uint32Array(len); window.crypto.getRandomValues(a)
      for (let i = 0; i < len; i++) out += chars[a[i] % chars.length]
    } else {
      for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)]
    }
    setF(prev => ({ ...prev, code: out, usageMode: 'single' }))
  }

  async function submit() {
    setBusy(true); setErr(null)
    const body = {
      code: f.code, description: f.description || null, benefitType: f.benefitType,
      amountOff: f.benefitType === 'amount' ? Number(f.amountOff) : null,
      percentOff: f.benefitType === 'percent' ? Number(f.percentOff) : null,
      maxDiscount: f.benefitType === 'percent' && f.maxDiscount !== '' ? Number(f.maxDiscount) : null,
      minSpend: (f.benefitType !== 'addon') && f.minSpend !== '' ? Number(f.minSpend) : null,
      addonType: f.benefitType === 'addon' ? f.addonType : null,
      appliesTo: f.appliesTo, packageIds: [...f.packageIds],
      startsAt: f.startsAt || null, endsAt: f.endsAt || null,
      usageMode: f.usageMode, isActive: f.isActive,
      assignedEmail: f.usageMode === 'single' ? (f.assignedEmail.trim() || null) : null,
      customerMessage: f.usageMode === 'single' ? (f.customerMessage || null) : null,
      sendEmailToCustomer: f.usageMode === 'single' && !!f.assignedEmail.trim() && f.sendEmailToCustomer,
      langId: f.emailLang,
    }
    const emailedNote = (sent) => sent ? ' ' + t('sp_promo_emailed', 'Code emailed to {email}.', { email: body.assignedEmail }) : ''
    try {
      if (code?.ID_NO) {
        const { data } = await tournamentApi.patch(`/portal/reseller/promo-codes/${code.ID_NO}`, body)
        onSaved(t('sp_promo_saved', 'Code saved.') + emailedNote(data?.emailSent))
      } else {
        const { data } = await tournamentApi.post('/portal/reseller/promo-codes', body)
        onSaved(t('sp_promo_created', 'Code created.') + emailedNote(data?.emailSent))
      }
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  const addonName = (a) => langId === 2 ? a.labelFr : a.labelEn
  const bt = f.benefitType

  return (
    <div style={overlay} onClick={busy ? undefined : onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>{code ? t('sp_promo_edit_title', 'Edit promo code') : t('sp_promo_new_title', 'New promo code')}</h2>

        <label style={lbl}>{t('sp_promo_f_code', 'Code')}
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={f.code} onChange={e => set('code', e.target.value.toUpperCase())} maxLength={40} placeholder="SUMMER10" style={{ ...inp, flex: 1, fontFamily: 'monospace', textTransform: 'uppercase' }} />
            <button type="button" onClick={generateCode} style={{ ...segOff, whiteSpace: 'nowrap', marginTop: 2 }}>{t('sp_promo_generate', 'Generate')}</button>
          </div>
        </label>

        <div style={{ ...fieldLbl, marginTop: 12 }}>{t('sp_promo_f_benefit', 'Benefit')}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          {[['percent', t('sp_promo_bt_percent', '% off')], ['amount', t('sp_promo_bt_amount', '$ off')], ['addon', t('sp_promo_bt_addon', 'Free add-on')]].map(([v, label]) => (
            <button key={v} type="button" onClick={() => set('benefitType', v)} style={bt === v ? segOn : segOff}>{label}</button>
          ))}
        </div>

        {bt === 'percent' && (
          <div style={row2}>
            <label style={lbl}>{t('sp_promo_f_percent', 'Percent off')}
              <input type="number" min="0" max="100" value={f.percentOff} onChange={e => set('percentOff', e.target.value)} style={inp} /></label>
            <label style={lbl}>{t('sp_promo_f_maxdisc', 'Max discount ($, optional)')}
              <input type="number" min="0" value={f.maxDiscount} onChange={e => set('maxDiscount', e.target.value)} style={inp} /></label>
          </div>
        )}
        {bt === 'amount' && (
          <div style={row2}>
            <label style={lbl}>{t('sp_promo_f_amount', 'Amount off ($)')}
              <input type="number" min="0" value={f.amountOff} onChange={e => set('amountOff', e.target.value)} style={inp} /></label>
            <label style={lbl}>{t('sp_promo_f_minspend', 'Minimum spend ($, optional)')}
              <input type="number" min="0" value={f.minSpend} onChange={e => set('minSpend', e.target.value)} style={inp} /></label>
          </div>
        )}
        {bt === 'addon' && (
          <label style={lbl}>{t('sp_promo_f_addon', 'Included add-on')}
            <select value={f.addonType} onChange={e => set('addonType', e.target.value)} style={inp}>
              {meta.addonTypes.map(a => <option key={a.key} value={a.key}>{addonName(a)}</option>)}
            </select>
          </label>
        )}

        <div style={{ ...fieldLbl, marginTop: 12 }}>{t('sp_promo_f_applies', 'Applies to')}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button type="button" onClick={() => set('appliesTo', 'all')} style={f.appliesTo === 'all' ? segOn : segOff}>{t('sp_promo_all_packages', 'All packages')}</button>
          <button type="button" onClick={() => set('appliesTo', 'selected')} style={f.appliesTo === 'selected' ? segOn : segOff}>{t('sp_promo_selected', 'Selected packages')}</button>
        </div>
        {f.appliesTo === 'selected' && (
          <>
            {(meta.labels || []).length > 0 && (
              <select value={labelFilter} onChange={e => setLabelFilter(e.target.value)} style={{ ...inp, marginBottom: 6 }}>
                <option value="">{t('sp_promo_all_labels', 'All labels')}</option>
                {meta.labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              {(() => {
                if (meta.packages.length === 0) return <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_promo_no_packages', 'You have no packages yet.')}</div>
                const shown = labelFilter ? meta.packages.filter(p => (p.labelIds || []).includes(Number(labelFilter))) : meta.packages
                if (shown.length === 0) return <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_promo_no_labeled', 'No packages with this label.')}</div>
                return shown.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={f.packageIds.has(p.id)} onChange={() => togglePkg(p.id)} />
                    <span>{p.name} <span style={{ color: '#94a3b8', fontSize: 12 }}>· {p.groupingName}</span></span>
                  </label>
                ))
              })()}
            </div>
          </>
        )}

        <div style={row2}>
          <label style={lbl}>{t('sp_promo_f_start', 'Start date (optional)')}
            <input type="date" value={f.startsAt} onChange={e => set('startsAt', e.target.value)} style={inp} /></label>
          <label style={lbl}>{t('sp_promo_f_end', 'End date (optional)')}
            <input type="date" value={f.endsAt} onChange={e => set('endsAt', e.target.value)} style={inp} /></label>
        </div>

        <label style={{ ...lbl, marginTop: 12 }}>{t('sp_promo_f_usage', 'How many times can it be used?')}
          <select value={f.usageMode} onChange={e => set('usageMode', e.target.value)} style={inp}>
            <option value="unlimited">{t('sp_promo_use_unlimited', 'Unlimited')}</option>
            <option value="once_per_customer">{t('sp_promo_use_once', 'Once per customer')}</option>
            <option value="single">{t('sp_promo_use_single', 'Single use (one total)')}</option>
          </select>
        </label>

        {f.usageMode === 'single' && (
          <>
            <label style={{ ...lbl, marginTop: 10 }}>{t('sp_promo_f_email', 'Customer email (optional)')}
              <input type="email" value={f.assignedEmail} onChange={e => set('assignedEmail', e.target.value)} placeholder="name@example.com" style={inp} />
              <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{t('sp_promo_email_help', 'A single-use code sent to a customer only works for this email.')}</span>
            </label>
            {f.assignedEmail.trim() && (
              <>
                <div style={{ ...fieldLbl, marginTop: 10 }}>{t('sp_promo_email_lang', 'Email language')}</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                  {[[1, t('sp_lang_en', 'English')], [2, t('sp_lang_fr', 'French')]].map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setEmailLang(v)} style={f.emailLang === v ? segOn : segOff}>{label}</button>
                  ))}
                </div>
                <label style={{ ...lbl, marginTop: 10 }}>{t('sp_promo_f_message', 'Message to the customer')}
                  <textarea value={f.customerMessage} onChange={e => set('customerMessage', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 13, color: '#475569' }}>
                  <input type="checkbox" checked={f.sendEmailToCustomer} onChange={e => set('sendEmailToCustomer', e.target.checked)} />
                  {t('sp_promo_send_label', 'Email this code to the customer on save')}
                </label>
              </>
            )}
          </>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <Toggle checked={f.isActive} onChange={(v) => set('isActive', v)} />
          <span style={{ fontSize: 14 }}>{t('sp_promo_active', 'Active')}</span>
        </label>

        {err && <div style={{ ...errorBox, marginTop: 12, marginBottom: 0 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} disabled={busy} style={ghostBtn}>{t('cancel', 'Cancel')}</button>
          <button onClick={submit} disabled={busy || !f.code} style={{ ...primaryBtn, opacity: (busy || !f.code) ? 0.5 : 1 }}>
            {busy ? t('saving_ellipsis', 'Saving…') : t('save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  )
}

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, background: '#f8fafc' }
const td = { padding: '12px 14px', verticalAlign: 'middle' }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 14 }
const okBox = { padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 6, marginBottom: 14, fontSize: 13 }
const linkBtn = { background: 'none', border: 'none', color: 'var(--brand-600, #0f766e)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '4px 8px' }
const primaryBtn = { padding: '9px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }
const ghostBtn = { padding: '9px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }
const modal = { background: '#fff', borderRadius: 12, padding: 22, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }
const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' }
const fieldLbl = { fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, marginTop: 2 }
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }
const segOn = { padding: '7px 12px', background: '#0f172a', color: '#fff', border: '1px solid #0f172a', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const segOff = { padding: '7px 12px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
